'use client';

import {
  Box,
  Flex,
  freddyTheme,
  Tabs,
  TabsList,
  TabsListItem,
  TabsPanel,
  Typography
} from '@coolblue-development/freddy';
import selectProductSpecificationsTabTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/selectProductSpecificationsTabTrigger';
import viewElementTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/viewElementTrigger';
import { TrackWhenVisible, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import type { Product, PropertyGroup, SubProducts } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { ReactNode, useId } from 'react';

import { SearchField, SearchProvider, SearchResult } from './Search';

type SpecificationsProps = {
  productId: number;
  propertyGroups?: PropertyGroup[];
  productTypeId: number;
  subProducts: Array<SubProducts | null>;
};

type SpecificationsSectionProps = {
  title: string;
  children: React.ReactNode;
};

type SingleProductSpecificationsProps = {
  productId: number;
  propertyGroups: PropertyGroup[];
  productTypeId: number;
};

type ComposedProductSpecificationsProps = {
  products: Product[];
};

export default function Specifications({
  productId,
  propertyGroups,
  productTypeId,
  subProducts
}: SpecificationsProps) {
  const { t } = useTranslation('webshop');
  const title = t('productPage.specifications');

  if (!propertyGroups) {
    return null;
  }

  const products = subProducts
    .map(subProduct => subProduct?.product ?? null)
    .filter(product => product !== null);

  if (subProducts.length > 0) {
    return (
      <SpecificationsSection title={title}>
        <ComposedProductSpecifications products={products} />
      </SpecificationsSection>
    );
  }

  return (
    <TrackWhenVisible trigger={viewElementTrigger({ sub_feature: 'specifications' })} threshold={0}>
      <SpecificationsSection title={title}>
        <SingleProductSpecifications
          productId={productId}
          propertyGroups={propertyGroups}
          productTypeId={productTypeId}
        />
      </SpecificationsSection>
    </TrackWhenVisible>
  );
}

function SingleProductSpecifications({
  productId,
  propertyGroups,
  productTypeId
}: SingleProductSpecificationsProps) {
  return (
    <SearchProvider>
      <SearchBox>
        <SearchField productId={productId} />
      </SearchBox>

      <SearchResult
        productId={productId}
        productTypeId={productTypeId}
        propertyGroups={propertyGroups}
      />
    </SearchProvider>
  );
}

function ComposedProductSpecifications({ products }: ComposedProductSpecificationsProps) {
  const tabsTranslations = useFreddyTranslations('tabs');
  const { trackEvent } = useAnalytics();

  function handleTabChange(value: string) {
    const params = {
      index: products.findIndex(product => product?.id === Number(value)) + 1,
      total_indices: products.length,
      item_id: Number(value)
    };
    trackEvent(selectProductSpecificationsTabTrigger(params));
  }

  return (
    <SearchProvider>
      <Tabs
        translations={tabsTranslations}
        defaultValue={products[0]?.id.toString()}
        onChange={value => handleTabChange(value)}
      >
        <TabsList>
          {products.map(product => (
            <TabsListItem
              key={product.id}
              value={product.id.toString()}
              startAdornment={
                <SquareProductImage
                  imageId={product.primaryImage.id}
                  background='transparent'
                  size='small'
                  alt='' // As the product name is displayed, the alt attribute is redundant here.
                  loading='lazy'
                />
              }
            >
              {product.name}
            </TabsListItem>
          ))}
        </TabsList>

        {products.map(product => (
          <TabsPanel key={product.id} value={product.id.toString()}>
            <Flex direction='column' gap={500}>
              <SearchBox>
                <SearchField productId={product.id} />
              </SearchBox>

              <SearchResult
                productId={product.id}
                productTypeId={Number(product.productType.productTypeId)}
                propertyGroups={product.properties?.propertyGroups ?? []}
              />
            </Flex>
          </TabsPanel>
        ))}
      </Tabs>
    </SearchProvider>
  );
}

function SpecificationsSection({ title, children }: SpecificationsSectionProps) {
  const titleId = useId();

  return (
    <Box hideUntil='md' component='section' id='product-specifications' aria-labelledby={titleId}>
      <Typography variant='h2' mb={400} id={titleId}>
        {title}
      </Typography>
      <Flex direction='column' gap={400} component='search' title={title}>
        {children}
      </Flex>
    </Box>
  );
}

const SearchBox = ({ children }: { children: ReactNode }) => (
  <Box sx={{ maxWidth: `calc(${freddyTheme.spacing[1000]} * 10)` }}>{children}</Box>
);
